<template>
  <div class="bulk-order-card-designs-index">
    <section class="card-designs__section">
      <PageHeadlineWithLink
        class="card-designs__head"
        title="利用できるカードデザイン一覧"
        link-title="戻る"
        @click-link="onClickBack"
      />
      <div>
        <div class="feedback">
          <transition name="fade" @enter="enterErrorFeedback">
            <feedback-error
              v-if="showableErrorFeedback"
              :message="errorMessage"
            />
          </transition>
        </div>
        <div
          v-resize="setStickyStyleIfNeeded"
          class="card-designs__categories-wrapper"
          :style="stickyStyle"
        >
          <div class="card-designs__categories">
            <div v-if="isLoadingCategories" class="card-designs__loading">
              <div class="loading">
                <div v-for="index in 12" :key="index" />
              </div>
            </div>
            <div v-else>
              <ul class="item-boxes--scrollable">
                <li
                  class="item-box"
                  :class="{ selected_category: isSelectedCategory('0') }"
                  @click="onClickCategory('0')"
                >
                  <p class="item-box__label">すべて</p>
                </li>
                <li
                  v-for="category in categories"
                  :key="category.id"
                  class="item-box"
                  :class="{
                    selected_category: isSelectedCategory(category.id)
                  }"
                  @click="onClickCategory(category.id)"
                >
                  <p class="item-box__label">
                    {{ category.name }}
                  </p>
                </li>
              </ul>
            </div>
          </div>
          <div class="card-designs__categories--pulldown">
            <p class="body card-categories-pulldown__label">
              カテゴリで絞り込む
            </p>
            <SortWithBlackChevronArea
              :options="categoriesForSort"
              :selected-value="selectedCategoryId"
              @change-sort="onClickCategory"
            />
          </div>
        </div>
        <div class="card-designs__items">
          <div v-if="isLoadingDesigns" class="card-designs__loading">
            <div class="loading loading--lg">
              <div v-for="index in 12" :key="index" />
            </div>
          </div>
          <div v-else>
            <div class="card-designs__item-boxes-wrapper">
              <!-- NOTE: preLoad = 読み込みを始める高さ (単位は比率っぽいけどよくわからず) -->
              <!-- TODO: loadingとerrorの画像は仮なので差し替える -->
              <div class="card-designs__item-boxes">
                <div
                  v-for="design in filteredCategories"
                  :key="design.id"
                  class="card-designs__item-box"
                  @click="onClickDesign(design)"
                >
                  <ImgLazy
                    :image-url="design.image_url"
                    :alt-text="design.name"
                    aspect-ratio="1"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <ImageModal
          v-if="showableModal && selectedDesign !== null"
          :image-url="imageUrl"
          :primary-button-text="'選択する'"
          :cancel-button-text="'閉じる'"
          :primary-button-disabled="modalPrimaryButtonDisabled"
          @clicked-modal-cover="closeModal"
          @clicked-primary="onClickedPrimary"
          @clicked-cancel="closeModal"
        />
      </div>
    </section>
  </div>
</template>

<script>
import ImageModal from '../../../molecules/modals/image-modal.vue'
import PageHeadlineWithLink from '../../../molecules/headlines/PageHeadlineWithLink.vue'
import FeedbackError from '../../../atoms/feedbacks/feedback-error.vue'
import axios from 'axios'
import SortWithBlackChevronArea from '../../../atoms/sorts/SortWithBlackChevronArea.vue'
import ImgLazy from '../../../atoms/images/ImgLazy.vue'
import { useBulkPersonalOrderSessionGiftCardStore } from '../../../../store/bulkPersonalOrderSessionGiftCard'
import { computed } from 'vue'

export default {
  components: {
    SortWithBlackChevronArea,
    ImageModal,
    FeedbackError,
    PageHeadlineWithLink,
    ImgLazy
  },
  props: {
    giftCardSessionToken: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const store = useBulkPersonalOrderSessionGiftCardStore()
    const { giftCardForms, setCardDesign } = store

    // サーバ側にギフトカードがある + ローカルストレージに編集中のデータがない場合は、ギフトカードリストに戻る
    // 直接URLを叩かない限り発生しないはず
    if (!giftCardForms[props.giftCardSessionToken]) {
      window.location.href = '/bulk_order/gift_cards'
    }

    const redirectUrl = computed(() => {
      if (props.giftCardSessionToken === 'new') {
        return '/bulk_order/gift_cards/new'
      }
      return `/bulk_order/gift_cards/${props.giftCardSessionToken}/edit`
    })

    return { redirectUrl, setCardDesign }
  },
  data() {
    return {
      selectedCategoryId: '0',
      selectedDesign: null,
      stickyStyle: {},
      showableModal: false,
      categories: [],
      categoriesForSort: [],
      designs: [],
      isLoadingDesigns: false,
      isLoadingCategories: false,
      showableErrorFeedback: false,
      errorMessage: '',
      needToScroll: false,
      modalPrimaryButtonDisabled: false
    }
  },
  computed: {
    filteredCategories() {
      if (this.selectedCategoryId === '0') {
        return this.designs
      }
      return this.designs.filter(design =>
        design.category_ids.includes(String(this.selectedCategoryId))
      )
    },
    imageUrl() {
      if (!this.selectedDesign || !this.selectedDesign.image_url) {
        return 'https://dummyimage.com/120x120/ccc/999.png&text=Not+Found'
      }
      return this.selectedDesign.image_url
    }
  },
  mounted() {
    this.setStickyStyleIfNeeded()
    this.getCardDesignCategories()
    this.getCardDesigns()
  },
  updated() {
    // モバイル表示でクエリパラメータから絞り込みを行った際に選択したカテゴリが表示されるようカテゴリの表示エリアをスクロールする。
    // カテゴリの取得をmountedで行っており、それ以降のライフサイクルでなければdomが存在しないため、updateで実行する。
    // updateはデータが更新されるたびに実行されるので、ユーザーの操作時にはスクロールされないよう、needToScrollで管理を行う。
    if (this.needToScroll) {
      this.scrollToSelectedCategory()
    }
  },
  methods: {
    onClickBack() {
      window.history.back()
    },
    onClickCategory(clicked_id) {
      this.selectedCategoryId = clicked_id
    },
    isSelectedCategory(id) {
      return this.selectedCategoryId === id
    },
    setStickyStyleIfNeeded() {
      const headers = document.querySelectorAll('header.header')
      if (headers.length !== 1) {
        return {}
      }
      const headerBottomRect = headers[0].getBoundingClientRect().bottom
      this.stickyStyle = {
        position: 'sticky',
        top: `${headerBottomRect}px`
      }
    },
    onClickDesign(design) {
      this.selectedDesign = design
      this.showableModal = true
    },
    closeModal() {
      this.showableModal = false
    },
    async getCardDesignCategories() {
      this.isLoadingCategories = true

      try {
        const res = await axios.get('/api/v1/pickup/card_design_categories')
        this.categories = res.data.data.map(card_design_categories => {
          return {
            id: card_design_categories.attributes.id.toString(),
            name: card_design_categories.attributes.name
          }
        })
        this.categoriesForSort = this.categories.map(v => ({
          key: v.name,
          value: v.id.toString()
        }))
        this.categoriesForSort = [{ key: 'すべて', value: '0' }].concat(
          this.categoriesForSort
        )
        // クエリパラメータで存在しないカテゴリIDが渡された場合の対応のため、カテゴリIDの取得が完了したあとにクエリパラメータからの絞り込みを行う
        this.selectQueryCategoryId()
        this.isLoadingCategories = false
      } catch (error) {
        this.isLoadingCategories = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    async getCardDesigns() {
      this.isLoadingDesigns = true

      try {
        const res = await axios.get('/api/v1/pickup/card_designs')
        this.designs = res.data.data.map(card_designs => {
          return {
            id: card_designs.attributes.id,
            name: card_designs.attributes.name,
            image_url: card_designs.attributes.image_url,
            category_ids:
              card_designs.relationships.card_design_categories.data.map(
                (_, index) => {
                  return card_designs.relationships.card_design_categories.data[
                    index
                  ].id
                }
              )
          }
        })
        this.isLoadingDesigns = false
      } catch (error) {
        this.isLoadingDesigns = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    async onClickedPrimary() {
      this.modalPrimaryButtonDisabled = true

      // ギフトカード情報の記録
      this.setCardDesign(this.giftCardSessionToken, {
        cardDesignId: this.selectedDesign.id,
        cardDesignImageUrl: this.selectedDesign.image_url
      })

      window.location.href = this.redirectUrl

      this.modalPrimaryButtonDisabled = false
    },
    enterErrorFeedback() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    },
    scrollToSelectedCategory() {
      document.querySelector('.item-boxes--scrollable').scrollTo(
        // 左側に項目が存在するカテゴリの場合に左側のカテゴリを少し表示するため、offsetの位置からずらしている。
        document.querySelector('.selected_category').offsetLeft - 100,
        0
      )
      this.needToScroll = false
    },
    selectQueryCategoryId() {
      const regexpMatchedQueryCategoryIds =
        window.location.href.match(/category_id=([0-9]+)/)
      if (!regexpMatchedQueryCategoryIds) {
        return
      }

      const queryCategoryId = regexpMatchedQueryCategoryIds[1]
      const isExistsCategory = this.categories.some(category => {
        return category.id === queryCategoryId
      })

      // 存在しないカテゴリIDが指定されている場合、「すべて」を表示する。
      if (!isExistsCategory) {
        return
      }

      this.onClickCategory(queryCategoryId)
      this.needToScroll = true
    }
  }
}
</script>

<style lang="scss" scoped>
.bulk-order-card-designs-index {
  .card-designs {
    &__head {
      margin-bottom: 2px;
    }

    &__title {
      font-size: 1.8rem;
      font-weight: bold;
      line-height: 1.6;
    }

    &__back {
      font-size: 12px;
      line-height: 1.6;
    }

    &__categories-wrapper {
      max-width: 100%;
      background: $white;
      z-index: 1;
    }

    &__categories {
      margin: auto;
      max-width: $max-contents-width;
      background: $white;

      @media all and (min-width: $base-contents-width) {
        display: none;
      }

      &--pulldown {
        max-width: $max-contents-width;
        margin: auto;
        padding: 0 16px;
        display: flex;
        align-items: center;

        @media all and (max-width: $base-contents-width - 1) {
          display: none;
        }

        .card-categories-pulldown__label {
          color: $gray-1;
          padding-right: 24px;
        }
      }

      .card-designs__loading {
        // @extend .mx-auto;
        margin: 0 auto;
        height: 60px;

        .loading {
          top: 20px;
        }
      }

      .item-box {
        transition: 0.3s;
        cursor: pointer;
        font-weight: bold;
        border-bottom: medium solid $gray-2;
        margin-right: 4px;
        width: auto;
        padding: 0;

        &.selected_category {
          transition: 0.3s;
          border-bottom: medium solid $primary;
        }

        &__label {
          font-size: 1.6rem;
          line-height: 1.6;
          color: $black;
          margin: 16px 24px;
        }
      }
    }

    &__items {
      .card-designs__loading {
        // @extend .mx-auto;
        margin: 0 auto;
        height: 100px;

        .loading {
          top: 40px;
        }
      }
    }

    &__item-boxes-wrapper {
      // @extend .mx-auto;
      margin: 0 auto;
      padding: 24px 16px;
      max-width: $max-contents-width;
    }

    &__item-boxes {
      // @extend .mx-0;
      margin: 0;
      width: 100%;
      overflow-x: auto;
      white-space: nowrap;
      -webkit-overflow-scrolling: touch;
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      grid-gap: 16px;
    }

    &__item-box {
      max-width: none;
      height: 100%;
      // NOTE: inline-block 要素間の空白や改行などにあたる暗黙的なスタイルを回避するための対応
      // https://www.codeblocq.com/2016/09/The-mysterious-4px-gap-in-between-images/
      font-size: 0;

      img {
        width: 100%;
      }
    }

    // Tablet
    @media screen and (max-width: $base-contents-width - 1) {
      &__item-boxes-wrapper {
        padding: 16px 0;
      }

      &__item-boxes {
        grid-template-columns: repeat(4, 1fr);
        grid-gap: 2px;
      }
    }

    // iPhoneX
    @media screen and (max-width: 474px) {
      &__item-boxes {
        grid-template-columns: repeat(3, 1fr);
        grid-gap: 2px;
      }
    }

    // iPhoneSE 未満
    @media screen and (max-width: 319px) {
      &__item-boxes {
        grid-template-columns: repeat(2, 1fr);
        grid-gap: 2px;
      }
    }
  }

  .item-boxes--scrollable {
    margin: 0;
    padding: 0;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}
</style>
